import React, { Component } from 'react';
import { Modal, Form, Select, Input, Radio, Checkbox, DatePicker, notification } from 'antd';
import moment from 'moment';
import Axios from 'axios';
import LS_SERVICE from '../../../utils/localStorage';
import {
  CONCEPTION_OPTIONS,
  DELIVERY_OPTIONS,
  PREGNANCY_OPTIONS,
  OUTCOME_OPTIONS,
  PLACE_OF_DELIVERY_OPTIONS,
  Save_Previous_Pregnancy_Details,
  Get_Previous_Pregnancy_Details,
  Delete_Previous_Pregnancy_Details,
  loadObsGynaeMasters,
} from '../../../utils/constant';

const { Option } = Select;
const { TextArea } = Input;

// Gender options for a fetus's birth details.
const GENDER_OPTIONS = [
  { val: '1', label: 'Male' },
  { val: '2', label: 'Female' },
  { val: '3', label: 'Other' },
];

// Outcomes (Live Term / Live Pre-Term) that make the per-fetus birth details mandatory.
const LIVE_OUTCOMES = ['1', '2'];

// Outcome is common to the whole delivery (like DeliveryDetails), so it is NOT
// stored per fetus here; each fetus only carries its own birth details.
const blankFetus = () => ({
  fetus_no: null,
  delivery_gender: undefined,
  delivery_bith_date_time: null,
  delivery_birth_weight: '',
  delivery_birth_height: '',
  delivery_birth_head_cc: '',
  delivery_congenital_abnormality: false,
  delivery_congenital_abnormality_remarks: '',
});

// Builds the add/edit form field values from a saved record. Shared by the constructor (so an
// edit opened directly from the page prefills on the very first render, with no list flash)
// and openEditForm (edits started from the in-popup list).
const buildEditState = (rec) => {
  const fetuses = (rec.fetuses && rec.fetuses.length > 0)
    ? rec.fetuses.map((f) => ({
      fetus_no: f.fetus_no,
      delivery_gender: f.delivery_gender != null ? String(f.delivery_gender) : undefined,
      delivery_bith_date_time: f.delivery_bith_date_time || null,
      delivery_birth_weight: f.delivery_birth_weight || '',
      delivery_birth_height: f.delivery_birth_height || '',
      delivery_birth_head_cc: f.delivery_birth_head_cc || '',
      delivery_congenital_abnormality: (f.delivery_congenital_abnormality === true || f.delivery_congenital_abnormality === 'true'),
      delivery_congenital_abnormality_remarks: f.delivery_congenital_abnormality_remarks || '',
    }))
    : [blankFetus()];

  // Outcome is saved at the record level now, but older records may only carry it on the
  // first fetus. Prefer the record-level value and fall back to the first fetus for those.
  const outcome = rec.delivery_outcome || (rec.fetuses && rec.fetuses[0] && rec.fetuses[0].delivery_outcome);

  return {
    errors: {},
    editingId: rec.id,
    pregnancy_year: rec.pregnancy_year != null ? parseInt(rec.pregnancy_year) : undefined,
    mode_of_conception: rec.mode_of_conception != null ? String(rec.mode_of_conception) : undefined,
    mode_of_delivery: rec.mode_of_delivery != null ? String(rec.mode_of_delivery) : undefined,
    caesarean_section_type: rec.caesarean_section_type || undefined,
    indication_for_c_section: rec.indication_for_c_section || '',
    place_of_delivery: rec.place_of_delivery != null ? String(rec.place_of_delivery) : undefined,
    delivery_outcome: outcome != null ? String(outcome) : undefined,
    pregnancy_type: rec.pregnancy_type != null ? String(rec.pregnancy_type) : undefined,
    no_of_fetus: fetuses.length,
    fetuses,
    antenatal_details: rec.antenatal_details || '',
    remarks: rec.remarks || '',
  };
};

/**
 * Previous Pregnancy Details popup.
 * Shows saved records in a table and an "Add Previous Pregnancy Details" form.
 * Data is stored patient-level in a dedicated table and fetched by patient_id.
 */
class PreviousPregnancyModal extends Component {
  constructor(props) {
    super(props);
    // Decide the open mode up front (in the constructor) so a direct Add/Edit shows ONLY the
    // form on the very first render - the list modal never flashes. The parent mounts this
    // component only when the popup opens, so props are already set here.
    const openEdit = !!props.editRecord;
    const openView = !openEdit && !!props.viewRecord;
    const openAdd = !openEdit && !openView && !!props.openAddFormOnOpen;
    const directAddMode = openEdit || openAdd;
    const editInit = openEdit ? buildEditState(props.editRecord) : {};
    this.state = {
      records: [],
      saving: false,
      // The add form opens in its own nested modal (kept off the main list to
      // avoid an over-long page).
      showAddForm: directAddMode,
      // True when the popup was opened straight into the Add/Edit form (via the parent's
      // "+ Previous Pregnancy Details" link or the inline table's edit icon). In this mode the
      // list modal is hidden and cancelling/saving closes the whole popup.
      directAddMode: directAddMode,
      // True when the popup was opened straight into the read-only detail view (via a click on
      // an inline table row). The list modal is hidden and closing the view closes the whole popup.
      directViewMode: openView,
      // When set, the "view record" nested modal shows this record's full details. Prefilled
      // when opened directly in view mode so the detail popup paints on the first render.
      viewRecord: openView ? props.viewRecord : null,
      // Id of the record currently being edited (null => the form is adding a new record).
      editingId: null,
      // Record pending soft-delete confirmation (null => no delete prompt open).
      deleteRecord: null,
      deleting: false,
      // Add-delivery form state
      pregnancy_year: undefined,
      mode_of_conception: undefined,
      mode_of_delivery: undefined,
      caesarean_section_type: undefined,
      indication_for_c_section: '',
      place_of_delivery: undefined,
      // Outcome is common to all fetuses (drives per-fetus mandatory fields).
      delivery_outcome: undefined,
      pregnancy_type: undefined,
      no_of_fetus: 1,
      fetuses: [blankFetus()],
      // Free-text notes captured per previous-pregnancy record.
      antenatal_details: '',
      remarks: '',
      errors: {},
      // When opening directly into Edit, prefill the form fields now (overrides the blanks
      // above) so the form renders populated on the first paint.
      ...editInit,
    };
  }

  componentDidMount() {
    // Obs/Gynae option masters are DB-backed: fetch fresh (single API call) and re-render.
    loadObsGynaeMasters().then(() => this.setState({ obsGynaeMastersLoaded: true }));
    this.fetchRecords();
    // The initial open mode (direct Add/Edit vs list) is already decided in the constructor,
    // so there's nothing to apply here on a fresh mount - that avoids a list-modal flash.
  }

  componentDidUpdate(prevProps) {
    // Refresh when the popup is (re)opened (covers the case where the component stays mounted).
    if (this.props.visible && !prevProps.visible) {
      this.resetForm();
      this.fetchRecords();
      this.applyOpenMode();
    }
  }

  // Decides what the popup shows on open:
  //  - editRecord set        -> open straight into the Edit form (directAddMode).
  //  - viewRecord set         -> open straight into the read-only detail view (directViewMode).
  //  - openAddFormOnOpen set  -> open straight into the Add form (directAddMode).
  //  - otherwise              -> show the records list.
  // In directAddMode/directViewMode the list modal is hidden and closing it closes the whole popup.
  applyOpenMode = () => {
    if (this.props.editRecord) {
      this.setState({ directViewMode: false, directAddMode: true }, () => {
        this.openEditForm(null, this.props.editRecord);
      });
    } else if (this.props.viewRecord) {
      this.setState({ directAddMode: false, directViewMode: true, viewRecord: this.props.viewRecord });
    } else if (this.props.openAddFormOnOpen) {
      this.setState({ directViewMode: false, showAddForm: true, directAddMode: true });
    } else {
      this.setState({ directAddMode: false, directViewMode: false });
    }
  }

  // Years available in the dropdown: NOT the current year, going back based on the patient's
  // age. A woman is assumed to be at least ~8 years old before a pregnancy could apply, so the
  // number of selectable years back = (patientAge - 8). E.g. age 24 => 24 - 8 = 16 years back,
  // i.e. from (currentYear - 1) down to (currentYear - 16). Falls back to 8 years when the age
  // is unknown or too small to derive a sensible range.
  getYearOptions = () => {
    const currentYear = moment().year();
    const age = parseInt(this.props.patientAge);
    let yearsBack = (!isNaN(age) && age > 8) ? (age - 8) : 8;
    if (yearsBack < 1) yearsBack = 1;
    const years = [];
    for (let y = currentYear - 1; y >= currentYear - yearsBack; y--) {
      years.push(y);
    }
    return years;
  }

  // When the pregnancy year changes, clear any previously-entered fetus birth date/time so a
  // stale date from a different year is not kept (the picker is constrained to the new year).
  onPregnancyYearChange = (value) => {
    this.setState((prev) => ({
      pregnancy_year: value,
      fetuses: prev.fetuses.map((f) => ({ ...f, delivery_bith_date_time: null })),
    }));
  }

  // Birth Date & Time is restricted to the selected pregnancy year: only dates within that
  // year are enabled, and future dates are never selectable. When no year is chosen yet, fall
  // back to simply blocking future dates.
  disabledBirthDate = (current) => {
    if (!current) return false;
    // Never allow future dates.
    if (current > moment().endOf('day')) return true;
    const year = parseInt(this.state.pregnancy_year);
    if (!isNaN(year)) {
      // Only allow dates that fall in the selected pregnancy year.
      return current.year() !== year;
    }
    return false;
  }

  // Opens the Birth Date picker on the selected pregnancy year (Jan of that year) so the user
  // lands in the correct year instead of the current month.
  birthDatePickerDefault = () => {
    const year = parseInt(this.state.pregnancy_year);
    if (!isNaN(year)) {
      return moment({ year: year, month: 0, day: 1 });
    }
    return undefined;
  }

  fetchRecords = async () => {
    const patient_id = this.props.patientId;
    if (!patient_id) return;
    try {
      const res = await Axios.post(Get_Previous_Pregnancy_Details, { patient_id });
      if (res.data && res.data.status) {
        this.setState({ records: res.data.data || [] });
      }
    } catch (err) {
      // Non-blocking: leave the table empty on fetch failure.
    }
  }

  resetForm = () => {
    this.setState({
      showAddForm: false,
      editingId: null,
      pregnancy_year: undefined,
      mode_of_conception: undefined,
      mode_of_delivery: undefined,
      caesarean_section_type: undefined,
      indication_for_c_section: '',
      place_of_delivery: undefined,
      delivery_outcome: undefined,
      pregnancy_type: undefined,
      no_of_fetus: 1,
      fetuses: [blankFetus()],
      antenatal_details: '',
      remarks: '',
      errors: {},
    });
  }

  // Single & Ectopic force exactly one fetus; Multiple lets the user pick the count.
  onPregnancyTypeChange = (value) => {
    let count = this.state.no_of_fetus;
    if (value === '1' || value === '3') {
      count = 1;
    } else if (value === '2') {
      count = count > 1 ? count : 2;
    }
    this.setState({ pregnancy_type: value }, () => this.syncFetusCount(count));
  }

  onNoOfFetusChange = (value) => {
    const count = parseInt(value) || 1;
    this.syncFetusCount(count);
  }

  // Grows/shrinks the fetuses[] array to match count, preserving existing entries.
  syncFetusCount = (count) => {
    this.setState((prev) => {
      const fetuses = [...prev.fetuses];
      if (count > fetuses.length) {
        while (fetuses.length < count) fetuses.push(blankFetus());
      } else if (count < fetuses.length) {
        fetuses.length = count;
      }
      return { no_of_fetus: count, fetuses };
    });
  }

  onFetusField = (index, field, value) => {
    this.setState((prev) => {
      const fetuses = [...prev.fetuses];
      fetuses[index] = { ...fetuses[index], [field]: value };
      return { fetuses };
    });
  }

  // Per-fetus birth details are no longer mandatory for any outcome.
  isBirthMandatory = () => false;

  // Outcomes that have NO fetus/birth data: Spontaneous Abortion (5) and MTP (6).
  // For these, the per-fetus block (birth date/time, gender, weight, height, head CC) is hidden.
  isNoFetusOutcome = () => ['5', '6'].includes(String(this.state.delivery_outcome));

  validate = () => {
    const errors = {};
    const {
      pregnancy_year, mode_of_delivery, caesarean_section_type, pregnancy_type, delivery_outcome, fetuses,
    } = this.state;

    if (!pregnancy_year) errors.pregnancy_year = 'Year is required';
    if (!delivery_outcome) errors.delivery_outcome = 'Outcome is required';

    // LSCS => caesarean section type (emergency/elective) required.
    if (mode_of_delivery === '2' && !caesarean_section_type) {
      errors.caesarean_section_type = 'Select emergency or elective';
    }

    // Per-fetus birth fields are no longer mandatory for any outcome.

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  handleSave = async () => {
    if (this.state.saving) return;
    if (!this.validate()) {
      notification.error({ message: 'Please fill all mandatory fields.', placement: 'topRight' });
      return;
    }

    const staffId = LS_SERVICE.get('staff_id');
    const doctorName = LS_SERVICE.get('profile')?.data?.full_name || ('Dr. ' + LS_SERVICE.get('staff_name'));

    // Drop fully-blank fetus rows (no birth details captured) so blank padding rows aren't
    // persisted — those caused an extra empty row + wrong fetus numbering in the view.
    const filledFetuses = this.state.fetuses.filter((fetus) => fetus && (
      (fetus.delivery_gender != null && fetus.delivery_gender !== '') ||
      (fetus.delivery_bith_date_time != null && fetus.delivery_bith_date_time !== '') ||
      (fetus.delivery_birth_weight != null && fetus.delivery_birth_weight !== '') ||
      (fetus.delivery_birth_height != null && fetus.delivery_birth_height !== '') ||
      (fetus.delivery_birth_head_cc != null && fetus.delivery_birth_head_cc !== '') ||
      fetus.delivery_congenital_abnormality === true ||
      (fetus.delivery_congenital_abnormality_remarks != null && fetus.delivery_congenital_abnormality_remarks !== '')
    ));

    const payload = {
      previous_pregnancy_data: {
        // When editing, send the existing record id so the backend updates in place.
        ...(this.state.editingId ? { id: this.state.editingId } : {}),
        patient_id: this.props.patientId,
        doctor_id: staffId,
        hospital_id: this.props.hospitalId,
        enterprise_id: this.props.enterpriseId,
        reference_type: 0,
        // reference_id records which visit/encounter this was saved from.
        reference_id: this.props.referenceId,
        source: this.props.source || 'OP',
        pregnancy_year: this.state.pregnancy_year,
        mode_of_conception: this.state.mode_of_conception || null,
        mode_of_delivery: this.state.mode_of_delivery || null,
        caesarean_section_type: this.state.mode_of_delivery === '2' ? (this.state.caesarean_section_type || null) : null,
        indication_for_c_section: this.state.mode_of_delivery === '2' ? (this.state.indication_for_c_section || null) : null,
        place_of_delivery: this.state.place_of_delivery || null,
        pregnancy_type: this.state.pregnancy_type,
        // Outcome is common to the whole delivery. Persist it at the RECORD level too (not
        // just on each fetus row) so it is saved and shown even when there is no fetus data
        // (e.g. Spontaneous Abortion / MTP, or when no per-fetus birth details were entered).
        delivery_outcome: this.state.delivery_outcome || null,
        antenatal_details: this.state.antenatal_details || null,
        remarks: this.state.remarks || null,
        // No. of Fetus follows the actual number of fetus blocks with data (filled rows), not
        // the raw no_of_fetus field, so it stays consistent with what is persisted/shown.
        no_of_fetus: filledFetuses.length,
        // Who added this record (id + full name with title).
        added_by_id: staffId,
        added_by_name: doctorName,
        created_by: staffId,
        updated_by: staffId,
        // fetus_no is assigned off the FILTERED index so numbering stays sequential.
        fetuses: filledFetuses.map((fetus, i) => ({
          fetus_no: i + 1,
          // Outcome is common to the delivery; store it on every fetus row.
          delivery_outcome: this.state.delivery_outcome || null,
          delivery_gender: fetus.delivery_gender || null,
          delivery_bith_date_time: fetus.delivery_bith_date_time
            ? moment(fetus.delivery_bith_date_time).format('YYYY-MM-DD HH:mm:ss')
            : null,
          delivery_birth_weight: fetus.delivery_birth_weight || null,
          delivery_birth_height: fetus.delivery_birth_height || null,
          delivery_birth_head_cc: fetus.delivery_birth_head_cc || null,
          delivery_congenital_abnormality: fetus.delivery_congenital_abnormality ? 'true' : 'false',
          delivery_congenital_abnormality_remarks: fetus.delivery_congenital_abnormality_remarks || null,
        })),
      },
    };

    this.setState({ saving: true });
    try {
      const res = await Axios.post(Save_Previous_Pregnancy_Details, payload);
      if (res.data && res.data.status) {
        notification.success({ message: this.state.editingId ? 'Previous Pregnancy Details updated.' : 'Previous Pregnancy Details saved.', placement: 'topRight' });
        const wasDirectAdd = this.state.directAddMode;
        // Close the add modal and refresh the list.
        this.resetForm();
        this.setState({ showAddForm: false });
        this.fetchRecords();
        // When opened directly into the Add form, saving closes the whole popup and returns
        // to the page (whose inline table refreshes via onClose).
        if (wasDirectAdd && this.props.onClose) {
          this.setState({ directAddMode: false });
          this.props.onClose();
        }
      } else {
        notification.error({ message: res.data?.message || 'Unable to save.', placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: 'Unable to save Previous Pregnancy Details.', placement: 'topRight' });
    } finally {
      this.setState({ saving: false });
    }
  }

  labelFor = (options, val) => {
    const found = options.find((o) => String(o.val) === String(val));
    return found ? found.label : '';
  }

  // Builds the outer-table "Gender" summary from a record's fetus birth details.
  // Single fetus with a gender -> just the gender label (e.g. "Male").
  // Multiple fetuses -> "Fetus 1 : Male, Fetus 2 : Female".
  genderSummary = (rec) => {
    const fetuses = (rec && rec.fetuses) || [];
    const withGender = fetuses.filter((f) => f && f.delivery_gender != null && f.delivery_gender !== '');
    if (withGender.length === 0) return '-';
    if (withGender.length === 1) {
      return this.labelFor(GENDER_OPTIONS, withGender[0].delivery_gender) || '-';
    }
    return withGender
      .map((f, i) => `Fetus ${f.fetus_no || (i + 1)} : ${this.labelFor(GENDER_OPTIONS, f.delivery_gender) || '-'}`)
      .join(', ');
  }

  // Builds the outer-table "Weight" summary from a record's fetus birth details.
  // Single fetus with a weight -> just the value (e.g. "2.5"). Multiple fetuses ->
  // "Fetus 1 : 2.5, Fetus 2 : 2.7".
  weightSummary = (rec) => {
    const fetuses = (rec && rec.fetuses) || [];
    const withWeight = fetuses.filter((f) => f && f.delivery_birth_weight != null && f.delivery_birth_weight !== '');
    if (withWeight.length === 0) return '-';
    if (withWeight.length === 1) {
      return withWeight[0].delivery_birth_weight || '-';
    }
    return withWeight
      .map((f, i) => `Fetus ${f.fetus_no || (i + 1)} : ${f.delivery_birth_weight || '-'}`)
      .join(', ');
  }

  // Edit is only allowed on records added by the currently logged-in doctor. ANC-sourced
  // records (and records added by another doctor) are therefore not editable here.
  canEdit = (rec) => {
    if (!rec || rec.source_table === 'anc') return false;
    const staffId = LS_SERVICE.get('staff_id');
    if (staffId == null || rec.added_by_id == null) return false;
    return String(rec.added_by_id) === String(staffId);
  }

  // Opens the add/edit form pre-filled with the selected record's values (edits started from
  // the in-popup list). Save performs an update because editingId is set.
  openEditForm = (e, rec) => {
    if (e) e.stopPropagation();
    this.setState({ showAddForm: true, ...buildEditState(rec) });
  }

  // Delete is allowed under the same condition as edit: only dedicated previous-pregnancy
  // records added by the currently logged-in doctor (not ANC-sourced).
  canDelete = (rec) => this.canEdit(rec);

  // Opens the delete-confirmation modal for a record.
  askDeleteRecord = (e, rec) => {
    if (e) e.stopPropagation();
    this.setState({ deleteRecord: rec });
  }

  // Soft-deletes the record (backend sets deleted = 1 + deleted_by_id/name = logged user).
  confirmDeleteRecord = async () => {
    const rec = this.state.deleteRecord;
    if (!rec || this.state.deleting) return;

    const staffId = LS_SERVICE.get('staff_id');
    const doctorName = LS_SERVICE.get('profile')?.data?.full_name || ('Dr. ' + LS_SERVICE.get('staff_name'));

    this.setState({ deleting: true });
    try {
      const res = await Axios.post(Delete_Previous_Pregnancy_Details, {
        id: rec.id,
        deleted_by_id: staffId,
        deleted_by_name: doctorName,
      });
      if (res.data && res.data.status) {
        notification.success({ message: 'Previous Pregnancy Details deleted.', placement: 'topRight' });
        this.setState({ deleteRecord: null });
        this.fetchRecords();
      } else {
        notification.error({ message: res.data?.message || 'Unable to delete.', placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: 'Unable to delete Previous Pregnancy Details.', placement: 'topRight' });
    } finally {
      this.setState({ deleting: false });
    }
  }

  renderRecordsTable = () => {
    const { records } = this.state;
    return (
      <div className="table-responsive">
        <style>{`#prev-pregnancy-table, #prev-pregnancy-table * { white-space: nowrap !important; word-break: keep-all; }`}</style>
        {/* Keep every header and data row on one line. The responsive wrapper preserves
            horizontal scrolling when the table is wider than the modal viewport. */}
        <table
          className="table table-bordered"
          id="prev-pregnancy-table"
          style={{ whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' }}
        >
          <thead style={{ whiteSpace: 'nowrap' }}>
            <tr style={{ whiteSpace: 'nowrap' }}>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>YEAR</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>MODE OF CONCEPTION</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>MODE OF DELIVERY</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>PLACE OF DELIVERY</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>OUTCOME</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>PREGNANCY TYPE</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>GENDER</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>WEIGHT (kg)</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>INDICATION FOR C-SECTION</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>ADDED BY</th>
              <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>ACTION</th>
            </tr>
          </thead>
          <tbody style={{ whiteSpace: 'nowrap' }}>
            {records.length === 0 ? (
              <tr style={{ whiteSpace: 'nowrap' }}>
                <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }} colSpan={11}>No records found</td>
              </tr>
            ) : (
              records.map((rec, i) => (
                <tr
                  key={i}
                  style={{ cursor: 'pointer' }}
                  title="Click to view details"
                  onClick={() => this.setState({ viewRecord: rec })}
                >
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{rec.pregnancy_year}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(CONCEPTION_OPTIONS, rec.mode_of_conception)}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                    {this.labelFor(DELIVERY_OPTIONS, rec.mode_of_delivery)}
                    {rec.mode_of_delivery === '2' && rec.caesarean_section_type ? ` (${rec.caesarean_section_type})` : ''}
                  </td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(PLACE_OF_DELIVERY_OPTIONS, rec.place_of_delivery)}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                    {/* Outcome is common to the whole delivery and saved at the record level;
                        fall back to the first fetus for older records that only stored it
                        per fetus. This keeps the outcome visible even without fetus data. */}
                    {this.labelFor(OUTCOME_OPTIONS, rec.delivery_outcome || (rec.fetuses && rec.fetuses[0] && rec.fetuses[0].delivery_outcome)) || '-'}
                  </td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                    {this.labelFor(PREGNANCY_OPTIONS, rec.pregnancy_type)}
                  </td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.genderSummary(rec)}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.weightSummary(rec)}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{rec.indication_for_c_section}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{rec.added_by_name || (rec.source_table === 'anc' ? 'ANC Record' : '')}</td>
                  <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                    {this.canEdit(rec) ? (
                      <a
                        className="nostyle-link mr-2"
                        onClick={(e) => { e.preventDefault(); this.openEditForm(e, rec); }}
                      >
                        <i className="icon-edit"></i>
                      </a>
                    ) : null}
                    {this.canDelete(rec) ? (
                      <a
                        className="nostyle-link mr-2"
                        onClick={(e) => { e.preventDefault(); this.askDeleteRecord(e, rec); }}
                      >
                        <i className="icon_delete"></i>
                      </a>
                    ) : null}
                    {(!this.canEdit(rec) && !this.canDelete(rec)) ? '-' : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderFetusBlock = (fetus, index) => {
    const mandatory = this.isBirthMandatory();
    const fetusError = (this.state.errors.fetuses && this.state.errors.fetuses[index]) || {};
    const mark = mandatory ? <span className="text-danger"> *</span> : null;
    return (
      <div className="card p-3 mb-3" key={index} style={{ background: '#f7f9fc' }}>
        {this.state.fetuses.length > 1 && (
          <h6 className="text-primary">Fetus {index + 1}</h6>
        )}
        <div className="row">
          <div className="col-md-4 mb-3">
            <label>Birth Date &amp; Time{mark}</label>
            <DatePicker
              className="w-100"
              showNow={false}
              showTime={{ use12Hours: true, format: 'hh:mm A' }}
              format="DD MMM, YYYY hh:mm A"
              // Default the calendar to the selected pregnancy year so the user lands there.
              defaultPickerValue={this.state.pregnancy_year ? moment(String(this.state.pregnancy_year), 'YYYY') : undefined}
              value={fetus.delivery_bith_date_time ? moment(fetus.delivery_bith_date_time, 'YYYY-MM-DD HH:mm:ss') : null}
              onChange={(date) => this.onFetusField(index, 'delivery_bith_date_time', date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null)}
              disabledDate={(current) => {
                if (!current) return false;
                // Never allow future dates (blocks "now" / today onward).
                if (current > moment().endOf('day')) return true;
                // Restrict the birth date to the selected pregnancy year.
                if (this.state.pregnancy_year && current.year() !== parseInt(this.state.pregnancy_year, 10)) return true;
                return false;
              }}
            />
            {fetusError.delivery_bith_date_time && <div className="text-danger small">Required</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label>Gender{mark}</label>
            <Select
              className="w-100"
              placeholder="Select Gender"
              value={fetus.delivery_gender}
              onChange={(v) => this.onFetusField(index, 'delivery_gender', v)}
            >
              {GENDER_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
            {fetusError.delivery_gender && <div className="text-danger small">Required</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label>Birth Weight (kg){mark}</label>
            <Input
              value={fetus.delivery_birth_weight}
              onChange={(e) => this.onFetusField(index, 'delivery_birth_weight', e.target.value)}
            />
            {fetusError.delivery_birth_weight && <div className="text-danger small">Required</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label>Height (cm){mark}</label>
            <Input
              value={fetus.delivery_birth_height}
              onChange={(e) => this.onFetusField(index, 'delivery_birth_height', e.target.value)}
            />
            {fetusError.delivery_birth_height && <div className="text-danger small">Required</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label>Head Circumference (cm){mark}</label>
            <Input
              value={fetus.delivery_birth_head_cc}
              onChange={(e) => this.onFetusField(index, 'delivery_birth_head_cc', e.target.value)}
            />
            {fetusError.delivery_birth_head_cc && <div className="text-danger small">Required</div>}
          </div>
          <div className="col-md-4 mb-3">
            <Checkbox
              checked={fetus.delivery_congenital_abnormality}
              onChange={(e) => this.onFetusField(index, 'delivery_congenital_abnormality', e.target.checked)}
            >
              Congenital Abnormality
            </Checkbox>
          </div>
          {fetus.delivery_congenital_abnormality && (
            <div className="col-md-8 mb-3">
              <label>Congenital Abnormality Details</label>
              <TextArea
                rows={2}
                value={fetus.delivery_congenital_abnormality_remarks}
                onChange={(e) => this.onFetusField(index, 'delivery_congenital_abnormality_remarks', e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  renderAddForm = () => {
    const { errors } = this.state;
    // The rest of the form (Mode of Delivery, Place of Delivery, Pregnancy Type and everything
    // after) is revealed only once an Outcome is selected. Until then only Year, Mode of
    // Conception and Outcome are shown.
    const outcomeSelected = this.state.delivery_outcome != null && this.state.delivery_outcome !== '';
    return (
      <div className="container-fluid emrfrm">
        {/* Sequence: Year, Mode of Conception, Outcome. */}
        <div className="row">
          <div className="col-md-4 mb-3">
            <label>Year<span className="text-danger"> *</span></label>
            <Select
              className="w-100"
              placeholder="Select Year"
              value={this.state.pregnancy_year}
              onChange={(v) => this.onPregnancyYearChange(v)}
            >
              {this.getYearOptions().map((y) => (
                <Option key={y} value={y}>{y}</Option>
              ))}
            </Select>
            {errors.pregnancy_year && <div className="text-danger small">{errors.pregnancy_year}</div>}
          </div>
          <div className="col-md-4 mb-3">
            <label>Mode of Conception</label>
            <Select
              className="w-100"
              placeholder="Select Mode of Conception"
              value={this.state.mode_of_conception}
              onChange={(v) => this.setState({ mode_of_conception: v })}
            >
              {CONCEPTION_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
          </div>
          <div className="col-md-4 mb-3">
            <label>Outcome<span className="text-danger"> *</span></label>
            <Select
              className="w-100"
              placeholder="Select Outcome"
              value={this.state.delivery_outcome}
              onChange={(v) => this.setState({ delivery_outcome: v })}
            >
              {OUTCOME_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
            {errors.delivery_outcome && <div className="text-danger small">{errors.delivery_outcome}</div>}
          </div>
        </div>

        {
          /* Mode of Delivery, Place of Delivery, Pregnancy Type and the per-fetus blocks are
             shown only after an Outcome has been selected. */
          outcomeSelected
          ?
        <>
        <div className="row">
          {/* Mode of Delivery, Place of Delivery and Pregnancy Type are hidden for
              Spontaneous Abortion / MTP outcomes. */}
          {!this.isNoFetusOutcome() && (
          <>
          <div className="col-md-4 mb-3">
            <label>Mode of Delivery</label>
            <Select
              className="w-100"
              placeholder="Select Mode of Delivery"
              value={this.state.mode_of_delivery}
              onChange={(v) => this.setState({ mode_of_delivery: v, caesarean_section_type: undefined, indication_for_c_section: '' })}
            >
              {DELIVERY_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
          </div>
          <div className="col-md-4 mb-3">
            <label>Place of Delivery</label>
            <Select
              className="w-100"
              placeholder="Select Place of Delivery"
              value={this.state.place_of_delivery}
              onChange={(v) => this.setState({ place_of_delivery: v })}
            >
              {PLACE_OF_DELIVERY_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
          </div>
          <div className="col-md-4 mb-3">
            <label>Pregnancy Type</label>
            <Select
              className="w-100"
              placeholder="Select Pregnancy Type"
              value={this.state.pregnancy_type}
              onChange={this.onPregnancyTypeChange}
            >
              {PREGNANCY_OPTIONS.map((o) => (
                <Option key={o.val} value={o.val}>{o.label}</Option>
              ))}
            </Select>
            {errors.pregnancy_type && <div className="text-danger small">{errors.pregnancy_type}</div>}
          </div>
          </>
          )}

          {!this.isNoFetusOutcome() && this.state.mode_of_delivery === '2' && (
            <div className="col-12 mb-3">
              <div className="card p-3" style={{ background: '#fff7f0' }}>
                <label className="d-block mb-2">Caesarean Section<span className="text-danger"> *</span></label>
                <Radio.Group
                  value={this.state.caesarean_section_type}
                  onChange={(e) => this.setState({ caesarean_section_type: e.target.value })}
                >
                  <Radio value="emergency">Emergency</Radio>
                  <Radio value="elective">Elective</Radio>
                </Radio.Group>
                {errors.caesarean_section_type && <div className="text-danger small">{errors.caesarean_section_type}</div>}
                <div className="mt-3">
                  <label>Indication</label>
                  <TextArea
                    rows={2}
                    placeholder="Indication for caesarean section"
                    value={this.state.indication_for_c_section}
                    onChange={(e) => this.setState({ indication_for_c_section: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {this.state.pregnancy_type === '2' && (
            <div className="col-md-4 mb-3">
              <label>No. of Fetus</label>
              <Select
                className="w-100"
                value={this.state.no_of_fetus}
                onChange={this.onNoOfFetusChange}
              >
                {[2, 3, 4, 5].map((n) => (
                  <Option key={n} value={n}>{n}</Option>
                ))}
              </Select>
            </div>
          )}
        </div>

        {/* Hide the per-fetus birth details block for Spontaneous Abortion / MTP outcomes. */}
        {!this.isNoFetusOutcome() && (
          <div className="mt-2">
            {this.state.fetuses.map((fetus, index) => this.renderFetusBlock(fetus, index))}
          </div>
        )}

        <div className="row">
          <div className="col-md-6 mb-3">
            <label>Antenatal Details</label>
            <TextArea
              rows={3}
              placeholder="Antenatal Details"
              value={this.state.antenatal_details}
              onChange={(e) => this.setState({ antenatal_details: e.target.value })}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label>Remarks</label>
            <TextArea
              rows={3}
              placeholder="Remarks"
              value={this.state.remarks}
              onChange={(e) => this.setState({ remarks: e.target.value })}
            />
          </div>
        </div>
        </>
          :
          null
        }
      </div>
    );
  }

  // Read-only detail view for a single record (opened by clicking a row).
  renderViewModal = () => {
    const rec = this.state.viewRecord;
    if (!rec) return null;
    const boolAbn = (v) => (v === true || v === 'true');
    return (
      <Modal
        id="previous_pregnancy_view"
        className="previous_pregnancy_view"
        visible={!!rec}
        bodyStyle={{ padding: '20px' }}
        closable={false}
        width="95vw"
        style={{ maxWidth: '1600px', top: 20 }}
        title={`Pregnancy Details${rec.pregnancy_year ? ' - ' + rec.pregnancy_year : ''}`}
        footer={[
          <div className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }} key="footer">
            <button
              className="btn btn-outline-secondary px-5"
              onClick={() => {
                this.setState({ viewRecord: null });
                // When opened directly into the detail view (row click on the page), closing
                // it closes the whole popup rather than revealing the list behind it.
                if (this.state.directViewMode && this.props.onClose) {
                  this.setState({ directViewMode: false });
                  this.props.onClose();
                }
              }}
            >
              CLOSE
            </button>
          </div>,
        ]}
      >
        <div className="container-fluid emrfrm">
          <div className="row">
            <div className="col-md-4 mb-2"><strong>Year:</strong> {rec.pregnancy_year || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Mode of Conception:</strong> {this.labelFor(CONCEPTION_OPTIONS, rec.mode_of_conception) || '-'}</div>
            <div className="col-md-4 mb-2">
              <strong>Mode of Delivery:</strong> {this.labelFor(DELIVERY_OPTIONS, rec.mode_of_delivery) || '-'}
              {rec.mode_of_delivery === '2' && rec.caesarean_section_type ? ` (${rec.caesarean_section_type})` : ''}
            </div>
            <div className="col-md-4 mb-2"><strong>Place of Delivery:</strong> {this.labelFor(PLACE_OF_DELIVERY_OPTIONS, rec.place_of_delivery) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Outcome:</strong> {this.labelFor(OUTCOME_OPTIONS, rec.delivery_outcome || (rec.fetuses && rec.fetuses[0] && rec.fetuses[0].delivery_outcome)) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Pregnancy Type:</strong> {this.labelFor(PREGNANCY_OPTIONS, rec.pregnancy_type) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>No. of Fetus:</strong> {rec.no_of_fetus || '-'}</div>
            {rec.mode_of_delivery === '2' && (
              <div className="col-md-4 mb-2"><strong>Indication for C-Section:</strong> {rec.indication_for_c_section || '-'}</div>
            )}
            <div className="col-md-4 mb-2"><strong>Added By:</strong> {rec.added_by_name || (rec.source_table === 'anc' ? 'ANC Record' : '-')}</div>
            <div className="col-md-12 mb-2"><strong>Antenatal Details:</strong> {rec.antenatal_details || '-'}</div>
            <div className="col-md-12 mb-2"><strong>Remarks:</strong> {rec.remarks || '-'}</div>
          </div>

          {(rec.fetuses || []).length > 0 && (
            <div className="table-responsive mt-3">
              <style>{`#previous-pregnancy-fetus-table th, #previous-pregnancy-fetus-table td { white-space: nowrap !important; word-break: keep-all; }`}</style>
              <table
                id="previous-pregnancy-fetus-table"
                className="table table-bordered"
                style={{ whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' }}
              >
                <thead style={{ whiteSpace: 'nowrap' }}>
                  <tr style={{ whiteSpace: 'nowrap' }}>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>FETUS</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>OUTCOME</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>GENDER</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>BIRTH DATE &amp; TIME</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>WEIGHT (kg)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>HEIGHT (cm)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>HEAD CC (cm)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>CONGENITAL ABNORMALITY</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.fetuses.map((f, i) => {
                    // Only show fetus rows that have data in the view table
                    const hasData = f && (f.delivery_bith_date_time || f.delivery_gender || f.delivery_birth_weight || f.delivery_birth_height || f.delivery_birth_head_cc || f.delivery_congenital_abnormality);
                    if (!hasData) {
                      return null;
                    }
                    return (
                    <tr key={i}>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.fetus_no || (i + 1)}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(OUTCOME_OPTIONS, f.delivery_outcome) || '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(GENDER_OPTIONS, f.delivery_gender) || '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_bith_date_time ? moment(f.delivery_bith_date_time, 'YYYY-MM-DD HH:mm:ss').format('DD MMM, YYYY hh:mm A') : '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_weight || '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_height || '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_head_cc || '-'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                        {boolAbn(f.delivery_congenital_abnormality)
                          ? `Yes${f.delivery_congenital_abnormality_remarks ? ' - ' + f.delivery_congenital_abnormality_remarks : ''}`
                          : 'No'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  render() {
    const { showAddForm, directAddMode, directViewMode } = this.state;
    return (
      <>
        <Modal
          id="previous_pregnancy_details"
          className="previous_pregnancy_details"
          /* In directAddMode/directViewMode the popup opens straight into the Add form or the
             detail view, so the list modal stays hidden. */
          visible={this.props.visible && !directAddMode && !directViewMode}
          bodyStyle={{ padding: '25px 10px' }}
          closable={false}
          /* XXL width so the wider records table (now including OUTCOME) fits without
             any column data wrapping. Capped at 95vw so it never exceeds the viewport. */
          width="95vw"
          style={{ maxWidth: '1600px', top: 20 }}
          title={
            <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
              <span>Previous Pregnancy Details</span>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => { this.resetForm(); this.setState({ showAddForm: true, editingId: null, errors: {} }); }}
              >
                + Previous Pregnancy Details
              </button>
            </div>
          }
          footer={[
            <div className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }} key="footer">
              <button
                className="btn btn-outline-secondary px-5"
                onClick={this.props.onClose}
              >
                CLOSE
              </button>
            </div>,
          ]}
        >
          <div className="container-fluid emrfrm">
            {this.renderRecordsTable()}
          </div>
        </Modal>

        {/* Nested "Add Previous Pregnancy Details" modal (kept separate so the list stays short). */}
        <Modal
          id="previous_pregnancy_add"
          className="previous_pregnancy_add"
          visible={showAddForm}
          bodyStyle={{ padding: '25px 15px' }}
          closable={false}
          width="1000px"
          title={this.state.editingId ? 'Edit Previous Pregnancy Details' : 'Add Previous Pregnancy Details'}
          footer={[
            <div className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }} key="footer">
              <button
                className="btn btn-outline-secondary px-5 mr-2"
                onClick={() => {
                  this.setState({ showAddForm: false, editingId: null, errors: {} });
                  // When opened directly into the Add form, cancelling closes the whole popup
                  // rather than revealing the list behind it.
                  if (this.state.directAddMode && this.props.onClose) {
                    this.props.onClose();
                  }
                }}
              >
                CANCEL
              </button>
              <button
                className="btn btn-primary px-5"
                disabled={this.state.saving}
                onClick={this.handleSave}
              >
                {this.state.saving ? 'SAVING...' : 'SAVE'}
              </button>
            </div>,
          ]}
        >
          {showAddForm && this.renderAddForm()}
        </Modal>

        {/* Delete confirmation (soft-delete) */}
        <Modal
          id="previous_pregnancy_delete"
          visible={!!this.state.deleteRecord}
          closable={false}
          width="500px"
          title={false}
          footer={[
            <div className="text-center" key="footer">
              <button
                className="btn btn-outline-secondary px-5 mr-2"
                onClick={() => this.setState({ deleteRecord: null })}
              >
                CANCEL
              </button>
              <button
                className="btn btn-primary px-5"
                disabled={this.state.deleting}
                onClick={this.confirmDeleteRecord}
              >
                {this.state.deleting ? 'DELETING...' : 'DELETE'}
              </button>
            </div>,
          ]}
        >
          <div className="row">
            <div className="col-12 my-3 text-center">
              <h6>Are you sure you want to delete this previous pregnancy record?</h6>
            </div>
          </div>
        </Modal>

        {this.renderViewModal()}
      </>
    );
  }
}

export default PreviousPregnancyModal;
